/* Small storefront transport. Hardware research and compatibility rules stay on the server. */
(() => {
  'use strict';
  if (window.ElitePCPrivate?.fitCacheVersion === 4 && window.ElitePCPrivate.repairStockVersion >= 11 && window.ElitePCPrivate.priceCacheVersion === 6 && window.ElitePCPrivate.pickerFitVersion === 1) return;
  let session = null, sessionRequest = null;
  const cooldowns = new Map();
  const retryAt = (action = 'fit') => Math.max(cooldowns.get('session') || 0, cooldowns.get(action) || 0);
  const storageKey = 'elite:private-session:v1';
  try { session = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch {}
  async function decode(response, action = 'session', startedAt) {
    if (response.status === 429 || response.status === 503) {
      const delay = Number(response.headers.get('retry-after'));
      cooldowns.set(action, Date.now() + Math.max(15000, (Number.isFinite(delay) ? delay : 15) * 1000));
    }
    const data = await response.json();
    if (!response.ok || data?.success !== true) {
      const error = new Error(data?.message || 'Compatibility checks are temporarily unavailable.');
      error.code = data?.code; error.status = response.status;
      throw error;
    }
    if ((action === 'repair' || action === 'validate') && Number.isFinite(startedAt)) {
      // maxAgeMs already excludes server work. Count only transport time
      // against it, using the server's disjoint timing phases when available.
      const timing = response.headers?.get('server-timing') || '';
      const phases = new Map([...timing.matchAll(/(?:^|,)\s*(limit|snapshot|repair)\s*;\s*dur=([\d.]+)/g)].map(match => [match[1], Number(match[2])]));
      const validationMs = Number(timing.match(/(?:^|,)\s*request\s*;\s*dur=([\d.]+)/)?.[1]);
      const serverMs = action === 'validate' ? (Number.isFinite(validationMs) ? validationMs : 0)
        : phases.size === 3 ? [...phases.values()].reduce((sum, value) => sum + value, 0) : 0;
      const elapsed = Math.max(0, Date.now() - startedAt);
      const maxAge = Math.max(0, Math.min(15000, Number.isFinite(data.maxAgeMs) ? data.maxAgeMs : 0));
      data.expiresAt = startedAt + Math.min(elapsed, Number.isFinite(serverMs) ? serverMs : 0) + maxAge;
    }
    return data;
  }
  async function prepare() {
    if (Date.now() < (cooldowns.get('session') || 0)) throw new Error('Please wait before checking more combinations.');
    if (session?.token && session.expiresAt > Date.now() + 10000) return session.token;
    if (!sessionRequest) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 45000);
    sessionRequest = fetch('/apps/pc-configurator?action=session', {
      method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify(session?.token ? {_session:session.token} : {}), cache:'no-store', signal:controller.signal,
    }).then(decode).then(data => {
      session = {token:data.token, expiresAt:data.expiresAt};
      try { sessionStorage.setItem(storageKey, JSON.stringify(session)); } catch {}
      return session.token;
    }).finally(() => { clearTimeout(timer); sessionRequest = null; });
    }
    return sessionRequest;
  }
  // Speculative checks share the server's browse allowance. Reserve capacity
  // for the saved-build repair instead of letting prefetch trigger a cooldown.
  let browseSpend = [];
  function browseCost(action, body) {
    if (action === 'repair') return body.preparedSelection ? 6 : 60;
    if (action === 'fit') return Math.max(1, body.skus?.length || 1);
    if (action === 'compatibility') return 12;
    if (action === 'picker-prices') return 12;
    if (action === 'entry') return 18;
    if (action === 'budget') return Math.max(1, body.cases?.length || 1) * 4;
    return 0; // Validation has a separate server purchase allowance.
  }
  function canPrepare(action, body, priority = 0) {
    browseSpend = browseSpend.filter(entry => Date.now() - entry.at < 60000);
    const cost = browseCost(action, body);
    return !cost || priority >= 4 || browseSpend.reduce((sum, entry) => sum + entry.cost, 0) + cost <= (priority >= 2 ? 300 : 240);
  }
  async function request(action, body, options = {}, retried = false) {
    if (Date.now() < retryAt(action)) throw new Error('Please wait before checking more combinations.');
    const token = await prepare();
    if (options.signal?.aborted) throw new DOMException('Aborted','AbortError');
    const startedAt = Date.now();
    if (Number.isFinite(options.preparationPriority) && !canPrepare(action, body, options.preparationPriority)) {
      const error = new Error('Background preparation deferred for the current build.');
      error.code = 'preparation_deferred';
      throw error;
    }
    const cost = browseCost(action, body);
    if (cost) { browseSpend = browseSpend.filter(entry => startedAt - entry.at < 60000); browseSpend.push({ at: startedAt, cost }); }
    try {
      return await fetch('/apps/pc-configurator?action=' + encodeURIComponent(action), {
        method:'POST', cache:'no-store', headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({...body, _session:token}), signal:options.signal,
      }).then(response => decode(response, action, startedAt));
    } catch (error) {
      if (error.code === 'session_expired' && !retried) {
        session = null;
        try { sessionStorage.removeItem(storageKey); } catch {}
        return request(action, body, options, true);
      }
      throw error;
    }
  }
  // Cache only answers for exact selections, never hardware records or rules.
  // Shared in-flight answers let an opened picker reuse its advance request.
  // A picker table formats thousands of totals; reuse its immutable formatter.
  const privateMoneyFormatter = new Intl.NumberFormat('en-GB', {style:'currency',currency:'GBP'});
  const fitAnswers = new Map(), fitRequests = new Map();
  const fitStorageKey = 'elite:private-fit:v4';
  const publicFitAnswer = result => ({sku:result.sku,status:result.status,
    compatibilityStatus:result.compatibilityStatus,
    compatibilityWarnings:Array.isArray(result.compatibilityWarnings)?result.compatibilityWarnings.filter(x=>typeof x?.message==='string').slice(0,24).map(x=>({code:String(x.code||'verification'),component:String(x.component||'case'),message:x.message.slice(0,900)})):[],
    reasons:Array.isArray(result.reasons)?result.reasons.filter(value=>typeof value==='string').slice(0,4).map(value=>value.slice(0,500)):[],
    ...(result.price?.currency==='GBP'&&result.price.pcPricingApplied===true&&typeof result.price.amount==='number'&&Number.isFinite(result.price.amount)&&result.price.amount>0
      ?{price:{amount:result.price.amount,currency:'GBP',formatted:privateMoneyFormatter.format(result.price.amount),pcPricingApplied:true}}:{})});
  const accessoryKey=lines=>(lines||[]).map(line=>[line.component,line.sku,String(line.variantId)]).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b)));
  // Public whole-build totals have their own O(1) cache. A price is never a
  // compatibility/stock answer and cannot enable checkout.
  const priceAnswers = new Map(), priceRequests = new Map();
  const priceStorageKey = 'elite:retail-totals:v1:' + (window.ElitePCBuildPricing?.cacheKey || 'private');
  const priceKey = (selection,accessories=[]) => JSON.stringify([Object.entries(selection||{}).sort(([a],[b])=>a.localeCompare(b)),accessoryKey(accessories)]);
  const retailPrice = value => value?.currency==='GBP' && value.pcPricingApplied===true && Number.isFinite(value.amount) && value.amount>0
    ? {amount:value.amount,currency:'GBP',formatted:privateMoneyFormatter.format(value.amount),pcPricingApplied:true,...(/^[a-f0-9]{24}$/.test(value.pricingRevision)?{pricingRevision:value.pricingRevision}:{})} : null;
  // A complete table is retained per exact build, not per visible card. Scrolling
  // thousands of rows must not evict the prices needed by another component.
  const pickerTables = new Map(), pickerPriceRequests = new Map();
  const pickerComponentKeys = new Set(['case','mobo','cpu','ram','gpu','psu','cooler','wifi','sata_ssd','sata_hdd','nvme_ssd','windows_key','kettle_lead']);
  function pickerAccessoryCandidate(selection, option) {
    let accessories = selection.accessories || [];
    if (!option.sku) accessories = accessories.filter(line => line.component !== option.component);
    else if (['monitors','warranty'].includes(option.component)) accessories = [...accessories.filter(line => line.component !== option.component), option];
    else if (!accessories.some(line => line.component === option.component && line.sku === option.sku && String(line.variantId) === String(option.variantId))) accessories = [...accessories, option];
    return {selections:selection.selections, accessories};
  }
  function pickerTableRow(selections, accessories = []) {
    const key = priceKey(selections, accessories);
    let newest = null;
    for (const table of pickerTables.values()) {
      const row = table.rows.get(key);
      if (row?.displayUntil > Date.now() && (!newest || row.capturedAt > newest.capturedAt)) newest = row;
    }
    return newest;
  }
  function peripheralPrice(selections, accessories = []) {
    if(!accessories.length||!window.ElitePCPeripheralPrices?.alive())return null;
    const candidates=[()=>window.ElitePCPrepared?.estimate({selections,accessories:[]}),()=>window.ElitePCPrepared?.displayPrice?.(selections,[]),
      ()=>pickerTableRow(selections,[])?.price,()=>window.ElitePCRepairs?.preparedPickerPrice?.(selections,[]),()=>{const saved=priceAnswers.get(priceKey(selections,[]));return saved?.expiresAt>Date.now()?saved.price:null;}];
    for(const read of candidates){const result=window.ElitePCPeripheralPrices.estimate(read(),accessories);if(result)return result;}
    return null;
  }
  function pickerPriceState(selections, accessories = []) {
    if(peripheralPrice(selections,accessories))return 'ready';
    const row = pickerTableRow(selections, accessories);
    return row ? row.price ? 'ready' : 'unavailable' : null;
  }
  function rememberPickerTable(body, data, started) {
    const source = priceKey(body.selections, body.accessories);
    if (data?.version !== 1 || source !== priceKey(data.selection?.selections, data.selection?.accessories)
      || !Number.isFinite(data.capturedAt) || !Array.isArray(data.groups) || data.groups.length > 13
      || !Array.isArray(data.accessoryOptions) || data.accessoryOptions.length > 48) throw new Error('Price table unavailable');
    const maxAge = Math.max(0, Math.min(30000, Number.isFinite(data.maxAgeMs) ? data.maxAgeMs : 0));
    const rows = new Map(), displayUntil = started + 300000;
    const add = (candidate, pennies) => {
      if (pennies !== null && (!Number.isSafeInteger(pennies) || pennies <= 0)) throw new Error('Invalid price');
      rows.set(priceKey(candidate.selections, candidate.accessories), {price:pennies === null ? null : retailPrice({amount:pennies/100,currency:'GBP',pcPricingApplied:true,pricingRevision:data.pricingRevision}),capturedAt:data.capturedAt,displayUntil});
    };
    for (const group of data.groups) {
      if (!pickerComponentKeys.has(group.component) || !Array.isArray(group.options) || group.options.length > 6000) throw new Error('Invalid price group');
      for (const [sku, pennies] of group.options) {
        if (typeof sku !== 'string' || sku.length > 200) throw new Error('Invalid price option');
        const parts = {...body.selections};
        if (sku) parts[group.component] = sku; else delete parts[group.component];
        add({selections:parts,accessories:body.accessories},pennies);
      }
    }
    for (const row of data.accessoryOptions) {
      const option = (body.accessoryOptions || []).find(value => value.component === row.component && value.sku === row.sku && String(value.variantId) === String(row.variantId));
      if (!option) throw new Error('Unexpected accessory price');
      add(pickerAccessoryCandidate(body, option), row.pennies);
    }
    const table = pickerTables.get(source) || {rows:new Map(),packets:new Map()};
    for (const [key, row] of rows) if (!table.rows.has(key) || table.rows.get(key).capturedAt <= row.capturedAt) table.rows.set(key, row);
    pickerTables.delete(source); pickerTables.set(source, table);
    while (pickerTables.size > 4) pickerTables.delete(pickerTables.keys().next().value);
    return {table,expiresAt:started+maxAge};
  }
  async function pickerPrices(selection, accessoryOptions = [], options = {}) {
    if(options.signal?.aborted)throw new DOMException('Aborted','AbortError');
    if(accessoryOptions.length && pickerPricesReady(selection,accessoryOptions))return {success:true};
    const body = JSON.parse(JSON.stringify({...selection,accessoryOptions}));
    const source = priceKey(body.selections, body.accessories), packet = JSON.stringify(accessoryKey(accessoryOptions));
    const key = source + ':' + packet;
    const cached = pickerTables.get(source)?.packets.get(packet);
    if (cached > Date.now()) return {success:true};
    if (!pickerPriceRequests.has(key)) {
      const controller = new AbortController();
      let started, timer;
      const abort = () => controller.abort();
      options.signal?.addEventListener('abort',abort,{once:true});
      const pending = (async () => {
        // Session admission and pricing have independent finite deadlines.
        // A slow but valid session must not consume the entire price deadline.
        await prepare();
        if (controller.signal.aborted) throw new DOMException('Aborted','AbortError');
        started = Date.now();
        timer = setTimeout(() => controller.abort(), 45000);
        return request('picker-prices',body,{signal:controller.signal});
      })().then(data => {
        const {table,expiresAt} = rememberPickerTable(body,data,started);
        table.packets.set(packet,expiresAt);
        return {success:true};
      }).finally(() => {clearTimeout(timer);options.signal?.removeEventListener('abort',abort);pickerPriceRequests.delete(key);});
      pickerPriceRequests.set(key,pending);
    }
    return pickerPriceRequests.get(key);
  }
  function pickerPricesReady(selection, accessoryOptions = []) {
    if(accessoryOptions.length && accessoryOptions.every(option=>{const candidate=pickerAccessoryCandidate(selection,option);return !!peekPrice(candidate.selections,candidate.accessories);}))return true;
    return (pickerTables.get(priceKey(selection.selections,selection.accessories))?.packets.get(JSON.stringify(accessoryKey(accessoryOptions))) || 0) > Date.now();
  }
  function savePrices() {
    try { sessionStorage.setItem(priceStorageKey,JSON.stringify([...priceAnswers].filter(([,entry])=>entry.expiresAt>Date.now()).slice(-256))); } catch {}
  }
  function rememberPrice(selection,accessories,value,expiresAt,persist=true) {
    const price=retailPrice(value);
    if(!price || !Number.isFinite(expiresAt) || expiresAt<=Date.now()) return;
    const key=priceKey(selection,accessories);
    priceAnswers.delete(key);
    priceAnswers.set(key,{price,expiresAt:Math.min(expiresAt,Date.now()+30000)});
    while(priceAnswers.size>256)priceAnswers.delete(priceAnswers.keys().next().value);
    if(persist)savePrices();
  }
  try {
    const stored=JSON.parse(sessionStorage.getItem(priceStorageKey)||'[]');
    if(Array.isArray(stored))for(const [key,entry] of stored.slice(-256)) {
      const price=retailPrice(entry?.price);
      if(typeof key==='string' && key.length<=4096 && price && entry.expiresAt>Date.now() && entry.expiresAt<=Date.now()+30000)priceAnswers.set(key,{price,expiresAt:entry.expiresAt});
    }
  } catch {}
  function peekPrice(selection,accessories=[]) {
    const peripheral=peripheralPrice(selection,accessories);if(peripheral)return peripheral;
    const matrix=window.ElitePCRepairs?.preparedPickerPrice?.(selection,accessories);if(matrix)return matrix;
    const prepared=window.ElitePCPrepared?.estimate({selections:selection,accessories});if(prepared)return prepared;
    const shared=window.ElitePCQuoteTransport?.peek({selections:selection,accessories});
    if(shared?.expiresAt>Date.now()&&shared.validation?.success===true){const price=retailPrice(shared.validation.price);if(price)return price;}
    const key=priceKey(selection,accessories),entry=priceAnswers.get(key);
    if(entry?.expiresAt>Date.now())return {...entry.price};
    priceAnswers.delete(key);
    return pickerTableRow(selection,accessories)?.price || null;
  }
  async function prices(body,options={}) {
    if(options.signal?.aborted)throw new DOMException('Aborted','AbortError');
    const candidate=sku=>{const parts={...body.selections};if(sku)parts[body.component]=sku;else delete parts[body.component];return parts;};
    // Prices have their own fast server path. Compatibility research must not
    // hold them up; already completed fit answers still share this cache.
    const missing=[...new Set(body.skus)].filter(sku=>!peekPrice(candidate(sku),body.accessories)&&!priceRequests.has(priceKey(candidate(sku),body.accessories)));
    if(missing.length) {
      const started=Date.now(),controller=new AbortController(),timer=setTimeout(()=>controller.abort(),15000);
      const pending=request('fit',{...body,skus:missing,priceOnly:true},{signal:controller.signal}).then(data=>{
        if(!Array.isArray(data.options))throw new Error('Price unavailable');
        const deadline=started+Math.max(0,Math.min(30000,Number.isFinite(data.maxAgeMs)?data.maxAgeMs:0));
        for(const row of data.options)if(missing.includes(row.sku))rememberPrice(candidate(row.sku),body.accessories,row.price,deadline,false);
        savePrices();return data;
      }).finally(()=>{clearTimeout(timer);for(const sku of missing)priceRequests.delete(priceKey(candidate(sku),body.accessories));});
      for(const sku of missing)priceRequests.set(priceKey(candidate(sku),body.accessories),pending);
    }
    const pending=[...new Set(body.skus.map(sku=>priceRequests.get(priceKey(candidate(sku),body.accessories))).filter(Boolean))];
    await Promise.all(pending);
    if(options.signal?.aborted)throw new DOMException('Aborted','AbortError');
    return {success:true,options:body.skus.map(sku=>({sku,price:peekPrice(candidate(sku),body.accessories)}))};
  }
  // Navigation must not discard a just-confirmed answer. Keep only the small
  // public result and its original deadline, never the private hardware/rules.
  try {
    const saved = JSON.parse(sessionStorage.getItem(fitStorageKey) || '[]');
    if (Array.isArray(saved)) for (const row of saved.slice(-160)) {
      if (!Array.isArray(row)) continue;
      const [key, entry] = row;
      if (typeof key !== 'string' || key.length > 4096 || !Number.isFinite(entry?.expiresAt)
        || entry.expiresAt <= Date.now() || entry.expiresAt > Date.now() + 60000
        || typeof entry.data?.sku !== 'string' || !['compatible','incompatible'].includes(entry.data.status)) continue;
      fitAnswers.set(key, {data:publicFitAnswer(entry.data), expiresAt:entry.expiresAt});
      const parts=JSON.parse(key);
      rememberPrice(Object.fromEntries(parts[1]),parts[2].map(([component,sku,variantId])=>({component,sku,variantId})),entry.data.price,entry.expiresAt,false);
    }
  } catch {}
  function saveFits() {
    try {
      const rows = [...fitAnswers].filter(([,entry]) => entry.expiresAt > Date.now() && entry.data.status !== 'unknown').slice(-160);
      let json = JSON.stringify(rows);
      while (json.length > 160000 && rows.length) { rows.shift(); json = JSON.stringify(rows); }
      sessionStorage.setItem(fitStorageKey, json);
    } catch {}
  }
  function fitKey(selection, component, sku, accessories = []) {
    const candidate = {...selection};
    if (sku) candidate[component] = sku; else delete candidate[component];
    return JSON.stringify([component, Object.entries(candidate).sort(([a], [b]) => a.localeCompare(b)), accessoryKey(accessories)]);
  }
  function peekFit(selection, component, sku, minimumRemainingMs = 0, accessories = []) {
    const matrix=window.ElitePCRepairs?.preparedPickerFit?.(selection,component,sku,minimumRemainingMs,accessories);if(matrix)return matrix;
    const prepared=window.ElitePCPrepared?.fit(selection,component,sku,accessories);
    if(prepared && window.ElitePCPrepared.expiresAt>Date.now()+minimumRemainingMs)return prepared;
    const candidate={...selection};if(sku)candidate[component]=sku;else delete candidate[component];
    const shared=window.ElitePCQuoteTransport?.peek({selections:candidate,accessories}),checked=shared?.validation;
    if(shared?.expiresAt>Date.now()+minimumRemainingMs&&checked?.success===true){
      if(checked.compatible===true)return publicFitAnswer({sku,status:'compatible',compatibilityStatus:checked.compatibilityStatus,reasons:[],price:checked.price});
      const reasons=checked.componentIssues?.[component];
      if(checked.compatible===false&&Array.isArray(reasons)&&reasons.length)return publicFitAnswer({sku,status:'incompatible',compatibilityStatus:'incompatible',reasons,price:checked.price});
    }
    const key = fitKey(selection, component, sku, accessories);
    const entry = fitAnswers.get(key);
    if (entry && entry.expiresAt > Date.now()) {
      // A refresh can start early without removing the still-valid badge.
      return entry.expiresAt > Date.now() + minimumRemainingMs ? entry.data : null;
    }
    fitAnswers.delete(key);
    return null;
  }
  async function fit(body, options = {}) {
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const missing = [...new Set(body.skus)].filter(sku => !peekFit(body.selections, body.component, sku, options.minimumRemainingMs || 0, body.accessories)
      && !fitRequests.has(fitKey(body.selections, body.component, sku, body.accessories)));
    if (missing.length) {
      const started = Date.now();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      const pending = request('fit', {...body, skus:missing}, {signal:controller.signal, preparationPriority:options.preparationPriority}).then(data => {
        if (!Array.isArray(data.options)) throw new Error('Fit check unavailable');
        // Subtract the complete request time as a conservative allowance for
        // network transit. Server refreshes cannot extend an old answer's life.
        const maxAge = Math.max(0, Math.min(60000, Number.isFinite(data.maxAgeMs) ? data.maxAgeMs : 15000) - (Date.now() - started));
        for (const sku of missing) {
          const result = data.options.find(value => value.sku === sku);
          if (!result || !['compatible','incompatible','unknown'].includes(result.status)) continue;
          const answer = publicFitAnswer(result);
          const key = fitKey(body.selections, body.component, sku, body.accessories);
          fitAnswers.delete(key);
          fitAnswers.set(key, {data:answer,
            expiresAt:Date.now() + (result.status === 'unknown' ? Math.min(2000,maxAge) : maxAge)});
          const candidate={...body.selections};if(sku)candidate[body.component]=sku;else delete candidate[body.component];
          rememberPrice(candidate,body.accessories,answer.price,Date.now()+maxAge,false);
        }
        while (fitAnswers.size > 400) fitAnswers.delete(fitAnswers.keys().next().value);
        saveFits();
        savePrices();
        return data;
      }).finally(() => {
        clearTimeout(timer);
        for (const sku of missing) fitRequests.delete(fitKey(body.selections, body.component, sku, body.accessories));
      });
      for (const sku of missing) fitRequests.set(fitKey(body.selections, body.component, sku, body.accessories), pending);
    }
    const pending = [...new Set(body.skus.map(sku => fitRequests.get(fitKey(body.selections, body.component, sku, body.accessories))).filter(Boolean))];
    const responses = await Promise.all(pending);
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return {success:true, options:body.skus.map(sku => peekFit(body.selections, body.component, sku, 0, body.accessories)
      || responses.flatMap(data => data.options).find(option => option.sku === sku)
      || {sku, status:'unknown', reasons:['Please check this option again.']})};
  }
  const repairAnswers = new Map(), repairPreviews = new Map(), repairPending = new Map();
  let repairTimer, repairScheduledAt = 0, repairWarmTimes = [], repairPriorityTimes = [];
  const repairWarmRunning = new Set();
  const repairWarmJobs = new Map();
  function publishRepairUpdate() {
    if (typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
      window.dispatchEvent(new window.CustomEvent('elite:repair-updated'));
    }
  }
  const repairSourceKey = body => JSON.stringify([Object.entries(body.selections || {}).sort(([a],[b])=>a.localeCompare(b)),accessoryKey(body.accessories)]);
  const repairKey = body => JSON.stringify([Object.entries(body.selections || {}).sort(([a],[b])=>a.localeCompare(b)),
    body.accessories || [], [...(body.lockedKeys || [])].sort(), body.candidateSkus || null, body.accessoryCandidates || null]);
  function peekRepair(body) {
    const entry = repairAnswers.get(repairKey(body));
    if (entry?.expiresAt > Date.now()) return entry.data;
    repairAnswers.delete(repairKey(body)); return null;
  }
  function peekRepairForSelection(selection, minimumRemainingMs = 0, lockedKeys = []) {
    const source = repairSourceKey(selection);
    return [...repairAnswers.values()].filter(entry=>entry.source===source && entry.expiresAt>Date.now()+minimumRemainingMs
      && lockedKeys.every(key=>entry.data.suggestions?.[0]?.selection?.selections[key]===selection.selections[key]))
      .sort((a,b)=>(a.data.suggestions?.[0]?.changes?.length || 99)-(b.data.suggestions?.[0]?.changes?.length || 99))[0]?.data || null;
  }
  function repairPreparationState(selection, lockedKeys = []) {
    const source=repairSourceKey(selection), locks=JSON.stringify([...lockedKeys].sort());
    const ready=[...repairAnswers.values()].some(entry=>entry.source===source && entry.expiresAt>Date.now()+500
      && entry.data.suggestions?.some(suggestion=>lockedKeys.every(key=>suggestion.selection?.selections[key]===selection.selections[key])));
    if(ready)return 'ready';
    const jobs=[...repairWarmJobs.values()].filter(job=>JSON.stringify([...job.body.lockedKeys].sort())===locks && repairSourceKey(job.body)===source && job.isCurrent());
    if(jobs.some(job=>job.state==='pending' || !job.state))return 'pending';
    return jobs.some(job=>job.state==='unavailable')?'unavailable':jobs.some(job=>job.state==='error')?'error':'pending';
  }
  function repairPreparationProgress(selection) {
    const source = repairSourceKey(selection);
    return [...repairWarmJobs.values()].filter(job => repairSourceKey(job.body) === source && job.isCurrent()).map(job => ({
      locks: job.body.lockedKeys, state: job.state || 'pending', running: repairWarmRunning.has(job),
      error: job.state === 'error' ? job.errorCode : '', retryIn: Math.max(0, job.retry - Date.now())
    }));
  }
  function peekRepairPreview(body) {
    const key = repairKey(body), entry = repairPreviews.get(key);
    // A preview is only a suggestion for this exact build. Its original quote
    // expiry remains unchanged, so selecting an old preview rechecks it live.
    if (entry?.previewUntil > Date.now()) return entry.data;
    repairPreviews.delete(key); return null;
  }
  function repair(body, {signal,force=false,refresh=false,preparationPriority} = {}) {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted','AbortError'));
    const key = repairKey(body), cached = !force && !refresh && peekRepair(body);
    if (cached) return Promise.resolve(cached);
    const preview = !force && peekRepairPreview(body);
    if (force) { repairAnswers.delete(key); repairPreviews.delete(key); }
    const pendingKey = key+(force?':fresh':'');
    if (!repairPending.has(pendingKey)) {
      const started=Date.now(), controller=new AbortController();
      const timer=setTimeout(()=>controller.abort(),45000);
      const preparedSelection = preview?.repairVersion === 3 && body.lockedKeys?.length && preview.suggestions?.[0]?.selection;
      // The saved-page action has already found its repair. Refresh that exact
      // complete build, including any accessory replacements, without another
      // catalogue search. Checkout still performs its own live validation.
      const pagePlan = refresh && !body.lockedKeys?.length && preview?.suggestions?.[0];
      const load = pagePlan
        ? request('validate',{...pagePlan.selection,checkout:false,freshCheck:true},{signal:controller.signal}).then(validation=>({
          success:true,repairVersion:preview.repairVersion,searchedFullCatalogue:true,maxAgeMs:validation.maxAgeMs,expiresAt:validation.expiresAt,
          suggestions:validation.success && validation.compatible && validation.checkoutReady && validation.build?.allComponentsInStock === true
            ? [{...pagePlan,validation}] : [],
        })).catch(error=>{
          repairAnswers.delete(key);
          if(error.status===409)return {success:true,suggestions:[],maxAgeMs:0};
          throw error;
        })
        : request('repair',{...body,freshCheck:force,...(preparedSelection ? {preparedSelection} : {})},{signal:controller.signal,preparationPriority});
      const pending=load.then(data=>{
        const maxAge=Math.max(0,Math.min(15000,Number.isFinite(data.maxAgeMs)?data.maxAgeMs:0));
        const answer = {...data, expiresAt:Number.isFinite(data.expiresAt) ? Math.min(Date.now()+maxAge,data.expiresAt) : started+maxAge};
        if (data.suggestions?.length) {
          repairPreviews.delete(key);
          repairPreviews.set(key, {data:answer, source:repairSourceKey(body), previewUntil:Date.now()+120000});
          while(repairPreviews.size>12)repairPreviews.delete(repairPreviews.keys().next().value);
        } else {
          // Remove every alias of a plan that just failed its stock/fit recheck.
          for (const cache of [repairPreviews, repairAnswers]) for (const [alias, entry] of cache) {
            if (alias === key || (preview && entry.data === preview)) cache.delete(alias);
          }
        }
        if (data.suggestions?.length && answer.expiresAt>Date.now()) repairAnswers.set(key,{data:answer,source:repairSourceKey(body),expiresAt:answer.expiresAt});
        while(repairAnswers.size>12)repairAnswers.delete(repairAnswers.keys().next().value);
        publishRepairUpdate();
        return answer;
      }).finally(()=>{clearTimeout(timer);repairPending.delete(pendingKey);});
      repairPending.set(pendingKey,pending);
    }
    // A hidden modal must not abort a request shared with the current review.
    return repairPending.get(pendingKey).then(data=>{
      if(signal?.aborted)throw new DOMException('Aborted','AbortError');return data;
    });
  }
  function scheduleRepairWarm(delay = 100) {
    if (repairWarmRunning.size >= 3 || !repairWarmJobs.size) return;
    const at=Date.now()+delay;
    if (repairTimer && repairScheduledAt<=at) return;
    clearTimeout(repairTimer);
    repairScheduledAt=at;
    repairTimer=setTimeout(()=>{repairTimer=null;void runRepairWarm();},delay);
  }
  function refreshingRepair(job) {
    const preview = peekRepairPreview(job.searchBody || job.body);
    return !!preview?.suggestions?.[0]?.selection && (!job.body.lockedKeys.length || preview.repairVersion === 3);
  }
  async function runRepairWarm() {
    if (repairWarmRunning.size >= 3) return;
    const now=Date.now();
    for(const [key,job] of repairWarmJobs) if(now>job.until || !job.isCurrent())repairWarmJobs.delete(key);
    if(document.hidden || navigator.onLine===false || navigator.connection?.saveData) { scheduleRepairWarm(2000); return; }
    const jobs=[...repairWarmJobs.values()].sort((a,b)=>Number((b.priority||0)>=4)-Number((a.priority||0)>=4) || Number((b.priority||0)>=2)-Number((a.priority||0)>=2)
      || Number(refreshingRepair(b))-Number(refreshingRepair(a)) || (b.priority||0)-(a.priority||0) || a.last-b.last);
    const job=jobs.find(value=>{
      if (repairWarmRunning.has(value)) return false;
      const page = (value.priority || 0) >= 4;
      // The two picker slots never occupy the saved-build slot. Once the page
      // needs fixing, finish existing work but start no unrelated preparations.
      if (page) {
        if ([...repairWarmRunning].some(running => (running.priority || 0) >= 4 && running.isCurrent())) return false;
      } else {
        if (jobs.some(queued => (queued.priority || 0) >= 4) || repairWarmRunning.size >= 2) return false;
        if (repairWarmRunning.size && !refreshingRepair(value) && ((value.priority || 0) < 2
          || [...repairWarmRunning].some(running => (running.priority || 0) >= 2 && running.isCurrent()))) return false;
      }
      const preview = peekRepairPreview(value.searchBody || value.body);
      const pageRefresh = !value.body.lockedKeys.length && !!preview?.suggestions?.[0]?.selection;
      const body = { ...(value.searchBody || value.body), ...(refreshingRepair(value) ? { preparedSelection: preview.suggestions[0].selection } : {}) };
      if (!canPrepare(pageRefresh ? 'validate' : 'repair', body, value.priority || 0)) return false;
      const answer=peekRepair(value.searchBody || value.body)
        || peekRepairForSelection(value.body, 0, value.body.lockedKeys);
      // Live inventory checks can exceed seven seconds. Refresh the page plan
      // with twelve seconds left, preserving the original deadline throughout.
      return now>=value.retry && (!answer || answer.expiresAt-now<(page ? 12000 : 7000));
    });
    if(!job){scheduleRepairWarm(1000);return;}
    repairWarmTimes=repairWarmTimes.filter(time=>now-time<60000);
    repairPriorityTimes=repairPriorityTimes.filter(time=>now-time<60000);
    const preview=peekRepairPreview(job.searchBody || job.body);
    const pageRefresh=!job.body.lockedKeys?.length && !!preview?.suggestions?.[0]?.selection;
    const refresh=pageRefresh || preview?.repairVersion===3;
    // Leave capacity for foreground searches. Exact refreshes retain all
    // checks, use the server's browse quota, and never rescan the catalogue.
    const budget=(job.priority||0)>=2?repairPriorityTimes:repairWarmTimes;
    if(now<retryAt(pageRefresh?'validate':'repair') || (!refresh && (job.priority||0)<4 && budget.length>=4)) {scheduleRepairWarm(2000);return;}
    if(!refresh)budget.push(now);
    repairWarmRunning.add(job);job.last=now;job.state='pending';
    scheduleRepairWarm(1000);
    try {
      let result=await repair(job.searchBody || job.body,{refresh:true,preparationPriority:job.priority||0});
      if(job.fullSearchFallback && !result.suggestions?.length && (!result.searchedFullCatalogue || job.body.lockedKeys.length > 0) && job.body.candidateSkus && !job.searchBody && job.isCurrent()) {
        job.searchBody={...job.body};delete job.searchBody.candidateSkus;
        result=await repair(job.searchBody,{refresh:true,preparationPriority:job.priority||0});
      }
      job.state=result.suggestions?.length?'ready':'unavailable';
      job.retry=Date.now()+(result.suggestions?.length?1000:15000);
    } catch (error) {job.state='error';job.errorCode=String(error.code || error.status || error.name || 'unavailable');job.retry=Math.max(Date.now()+(error.code==='preparation_deferred'?1000:15000),retryAt('repair'),retryAt('validate'));}
    finally {repairWarmRunning.delete(job);publishRepairUpdate();scheduleRepairWarm(100);}
  }
  function prepareRepair(selection, lockedKeys = [], candidateSkus, accessoryCandidates, options = {}) {
    const body=JSON.parse(JSON.stringify({...selection,lockedKeys,...(candidateSkus ? {candidateSkus} : {}),...(accessoryCandidates ? {accessoryCandidates} : {})}));
    const key=repairKey(body);
    let existing=repairWarmJobs.get(key);
    for(const [otherKey,other] of repairWarmJobs) {
      if(otherKey!==key && JSON.stringify([...lockedKeys].sort())===JSON.stringify([...other.body.lockedKeys].sort())
        && repairSourceKey(other.body)===repairSourceKey(body)) {
        // Promote an in-flight landing/draft search instead of duplicating it
        // when the page supplies a different candidate shortlist.
        if (!existing && (repairWarmRunning.has(other) || ((other.priority||0)>(options.priority||0) && other.isCurrent()))) existing=other;
        repairWarmJobs.delete(otherKey);
      }
    }
    // A picker or landing request may have checked this exact build using a
    // different shortlist. Carry that result into the page job without a new
    // search or extending its original stock/price deadline.
    if(!peekRepair(body)) {
      const source=repairSourceKey(body), answer=peekRepairForSelection(body,0,lockedKeys);
      const preview=answer || [...repairPreviews.values()].find(entry=>entry.source===source && entry.previewUntil>Date.now()
        && lockedKeys.every(part=>entry.data.suggestions?.[0]?.selection?.selections[part]===body.selections[part]))?.data;
      if(answer)repairAnswers.set(key,{data:answer,source,expiresAt:answer.expiresAt});
      if(preview)repairPreviews.set(key,{data:preview,source,previewUntil:Date.now()+120000});
    }
    // A lower-priority consumer of the same answer cannot demote the page job
    // or replace its lifetime predicate while the saved build still needs it.
    if (existing?.isCurrent() && (existing.priority || 0) > (options.priority || 0)) {
      existing.until = Date.now() + 120000;
      repairWarmJobs.set(key, existing);
      scheduleRepairWarm(0);
      return;
    }
    const job=existing || {last:0,retry:0};
    Object.assign(job,{body:repairWarmRunning.has(job)?job.body:body,priority:options.priority||0,fullSearchFallback:options.fullSearchFallback===true,until:Date.now()+120000,isCurrent:options.isCurrent||(()=>true)});
    repairWarmJobs.set(key,job);
    while(repairWarmJobs.size>12)repairWarmJobs.delete(repairWarmJobs.keys().next().value);
    scheduleRepairWarm((options.priority||0)>=2?0:100);
  }
  function cancelRepairPreparation({lockedOnly=false}={}) {
    for(const [key,job] of repairWarmJobs)if(!lockedOnly || job.body.lockedKeys.length)repairWarmJobs.delete(key);
    if(!repairWarmJobs.size){clearTimeout(repairTimer);repairTimer=null;}
  }
  window.ElitePCPrivate = {request, prepare, retryAt, canPrepare, fit, peekFit, peekPrice, prices, pickerPrices, pickerPricesReady, pickerPriceState, rememberPrice, repair, peekRepair, peekRepairForSelection, repairPreparationState, repairPreparationProgress, peekRepairPreview, prepareRepair, cancelRepairPreparation, fitCacheVersion:4, repairStockVersion:11, priceCacheVersion:6, pickerFitVersion:1};
  window.ElitePCInitialRepair?.start();
})();
